import {
  useBulkCreateTables,
  useDeleteTable,
  useFloorPlan,
  useTables,
  useUpdateFloorPlan,
  useZones,
} from '@/features/reservation-setup/hooks/useReservationSetup';
import PushSheet from '@/components/PushSheet';
import {
  CHAIR_GAP,
  CHAIR_THICK,
  circleChairs,
  rectChairs,
} from '@/features/reservation-setup/utils/tableChairs';
import { tableDisplayName } from '@/features/reservation-setup/utils/tableNaming';
import { clampScale, snap, SNAP } from '@/features/reservation-setup/floor-plan-geometry';
import { useT } from '@/i18n';
import { useIsWideViewport } from '@/utils/useMediaQuery';
import type { FloorPlanElement, RestaurantTable } from '@restaurant/api-client';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type Konva from 'konva';
import { Circle, Group, Layer, Rect, Stage, Text } from 'react-konva';
import {
  Block,
  BlockTitle,
  Button,
  Link as F7Link,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Page,
  PageContent,
  Preloader,
  Segmented,
  Stepper,
  Toolbar,
  f7,
} from 'framework7-react';

// ─── Batch Create Sheet ───────────────────────────────────────────────────────
//
// Pattern: the <Toolbar> (a "fixed" sheet element f7-react positions specially)
// stays a direct child of <PushSheet>; the interactive form lives in a *child*
// component (`*Content`) which exposes its submit handler via `submitRef`. A
// state change inside the child re-renders only the child - not f7-react's
// <Sheet> - so the open, push-transformed sheet isn't thrashed.

type BatchRow = { seats: number; quantity: number };

function BatchCreateSheet({
  open,
  onClose,
  zoneId,
}: {
  open: boolean;
  onClose: () => void;
  zoneId: string | null;
}) {
  const t = useT();
  const rs = t.reservationSetup;
  const submitRef = useRef<() => void>(() => {});
  return (
    <PushSheet open={open} onClose={onClose} style={{ height: 'auto' }}>
      <Toolbar>
        <div className="left">
          <F7Link sheetClose>{t.cancel}</F7Link>
        </div>
        <div className="title">{rs.batchAddTables}</div>
        <div className="right">
          <F7Link onClick={() => submitRef.current()}>{t.createShort}</F7Link>
        </div>
      </Toolbar>
      <BatchCreateContent open={open} zoneId={zoneId} submitRef={submitRef} />
    </PushSheet>
  );
}

function BatchCreateContent({
  open,
  zoneId,
  submitRef,
}: {
  open: boolean;
  zoneId: string | null;
  submitRef: { current: () => void };
}) {
  const t = useT();
  const rs = t.reservationSetup;
  const bulkCreate = useBulkCreateTables();
  const [labelPrefix, setLabelPrefix] = useState('T');
  const [rows, setRows] = useState<BatchRow[]>([{ seats: 2, quantity: 4 }]);
  // Synchronous guard so a rapid double-tap on Create can't create two batches.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setLabelPrefix('T');
      setRows([{ seats: 2, quantity: 4 }]);
    }
  }, [open]);

  const addRow = () => setRows((r) => [...r, { seats: 2, quantity: 1 }]);
  const removeRow = (i: number) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i: number, patch: Partial<BatchRow>) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  submitRef.current = () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    bulkCreate.mutate(
      { zoneId: zoneId ?? undefined, labelPrefix, rows },
      {
        onSuccess: (res) => {
          f7.sheet.close();
          f7.toast.show({ text: rs.createdTables(res.created), closeTimeout: 2000 });
        },
        onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.failedBatchCreate),
        onSettled: () => {
          submittingRef.current = false;
        },
      },
    );
  };

  return (
    <PageContent style={{ maxHeight: '70vh' }}>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={rs.labelPrefix}
          placeholder="T"
          value={labelPrefix}
          onInput={(e) => setLabelPrefix((e.target as HTMLInputElement).value.slice(0, 8))}
          info={rs.labelPrefixExample}
        />
      </List>

      <BlockTitle>{rs.seatCategories}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {rows.map((row, i) => (
          <ListItem key={i} noChevron>
            <div
              slot="title"
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}
            >
              <i
                className="f7-icons"
                style={{
                  color: 'var(--f7-color-red)',
                  fontSize: 20,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
                onClick={() => removeRow(i)}
              >
                minus_circle_fill
              </i>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{ fontSize: 14, color: 'var(--f7-list-item-text-color)', flexShrink: 0 }}
                >
                  {rs.seats}
                </span>
                <Stepper
                  value={row.seats}
                  min={1}
                  max={20}
                  onStepperChange={(v: number) => updateRow(i, { seats: v })}
                  small
                  raisedMd
                />
                <span
                  style={{ fontSize: 14, color: 'var(--f7-list-item-text-color)', flexShrink: 0 }}
                >
                  {rs.qty}
                </span>
                <Stepper
                  value={row.quantity}
                  min={1}
                  max={200}
                  onStepperChange={(v: number) => updateRow(i, { quantity: v })}
                  small
                  raisedMd
                />
              </div>
            </div>
          </ListItem>
        ))}
        <ListItem>
          <F7Link slot="title" onClick={addRow}>
            {rs.addSeatCategory}
          </F7Link>
        </ListItem>
      </List>
    </PageContent>
  );
}

// ─── Tables tab ───────────────────────────────────────────────────────────────

function TablesContent({
  editMode,
  onEditModeChange,
  onSelectedCountChange,
}: {
  editMode: boolean;
  onEditModeChange: (v: boolean) => void;
  onSelectedCountChange: (n: number) => void;
}) {
  const t = useT();
  const rs = t.reservationSetup;
  const { data: tables, isLoading: tablesLoading } = useTables();
  const { data: zones } = useZones();
  const deleteTable = useDeleteTable();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchZoneId, setBatchZoneId] = useState<string | null>(null);

  // Editing a table is a classic pushed page (not a sheet) - push onto the
  // current view's stack so it slides in with native Back. See TableEditFormPage.
  const openEdit = (id: string) =>
    f7.views.current.router.navigate(`/settings/reservation/advanced-tables/${id}`);

  useEffect(() => {
    if (!editMode) setSelected(new Set());
  }, [editMode]);

  useEffect(() => {
    onSelectedCountChange(selected.size);
  }, [selected.size, onSelectedCountChange]);

  const zoneLabel = (zoneId: string | null) => zones?.find((z) => z.id === zoneId)?.label ?? null;

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const deleteSelected = () => {
    const ids = Array.from(selected);
    f7.dialog.confirm(
      rs.deleteTablesConfirm(ids.length, ids.length === 1 ? '' : 's'),
      rs.deleteTablesTitle,
      async () => {
        for (const id of ids) await deleteTable.mutateAsync(id).catch(() => {});
        setSelected(new Set());
        onEditModeChange(false);
        f7.toast.show({ text: rs.tablesDeleted(ids.length), closeTimeout: 2000 });
      },
    );
  };

  const openBatchCreate = (zoneId: string | null) => {
    setBatchZoneId(zoneId);
    setBatchOpen(true);
  };

  // Group by zone first (sortOrder, then "No zone" last), then within each
  // zone sub-group by seat count (ascending) so a 50-table mix of 2/4/6-tops
  // reads as three tidy sections instead of one long mixed list.
  const grouped = (() => {
    const all = tables ?? [];
    const byZone = new Map<string | null, RestaurantTable[]>();
    for (const t of all) {
      const key = t.zoneId;
      if (!byZone.has(key)) byZone.set(key, []);
      byZone.get(key)!.push(t);
    }
    const zoneEntries = Array.from(byZone.entries()).sort(([a], [b]) => {
      if (a === null) return 1;
      if (b === null) return -1;
      const za = zones?.find((z) => z.id === a)?.sortOrder ?? 0;
      const zb = zones?.find((z) => z.id === b)?.sortOrder ?? 0;
      return za - zb;
    });
    return zoneEntries.map(([zoneId, group]) => {
      const bySeats = new Map<number, RestaurantTable[]>();
      for (const t of group) {
        const arr = bySeats.get(t.seats) ?? [];
        arr.push(t);
        bySeats.set(t.seats, arr);
      }
      const seatGroups = Array.from(bySeats.entries())
        .sort(([a], [b]) => a - b)
        .map(([seats, items]) => ({
          seats,
          items: items.slice().sort((a, b) => a.ordinal - b.ordinal),
        }));
      return { zoneId, seatGroups };
    });
  })();

  return (
    <>
      {tablesLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}

      {!tablesLoading && tables?.length === 0 && (
        <Block strongIos strongMd outlineIos className="text-align-center">
          <p>{rs.noTablesConfigured}</p>
          <Button fill round onClick={() => openBatchCreate(null)}>
            {rs.batchAddTables}
          </Button>
        </Block>
      )}

      {!tablesLoading &&
        grouped.map(({ zoneId, seatGroups }) => {
          const label = zoneLabel(zoneId) ?? rs.noZoneLabel;
          return (
            <div key={zoneId ?? '__none__'}>
              <BlockTitle className="adv-tables-zone-title">
                <span style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{label}</span>
                  {editMode && (
                    <F7Link
                      iconF7="plus_circle"
                      style={{ flexShrink: 0, marginLeft: 8 }}
                      onClick={() => openBatchCreate(zoneId)}
                    />
                  )}
                </span>
              </BlockTitle>
              {seatGroups.map(({ seats, items }) => (
                <div key={`${zoneId ?? '__none__'}:${seats}`}>
                  {/* Small seat-count subheader. `medium` keeps it visually
                      lighter than the zone BlockTitle so the hierarchy reads
                      Zone → Seat-group → Tables. */}
                  <BlockTitle medium className="adv-tables-seat-group">
                    {rs.seater(seats)}
                    <span className="adv-tables-seat-group-count"> · {items.length}</span>
                  </BlockTitle>
                  <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                    {items.map((t) => {
                      const isSelected = selected.has(t.id);
                      // Fast-scan badges for any non-default state, so an
                      // operator can spot at a glance which tables won't count
                      // toward capacity or can't be booked online - without
                      // opening each one.
                      const badges = [
                        !t.isActive && { label: rs.inactiveBadge, tone: 'red' as const },
                        !t.countsForCapacity && {
                          label: rs.noCapacityBadge,
                          tone: 'orange' as const,
                        },
                        !t.isBookableOnline && { label: rs.notOnlineBadge, tone: 'gray' as const },
                      ].filter((b): b is { label: string; tone: 'red' | 'orange' | 'gray' } =>
                        Boolean(b),
                      );
                      return (
                        <ListItem
                          key={t.id}
                          title={tableDisplayName(t, zoneLabel(zoneId))}
                          after={rs.seatsCount(t.seats)}
                          link="#"
                          noChevron={editMode}
                          onClick={() => (editMode ? toggleSelect(t.id) : openEdit(t.id))}
                          style={{ opacity: t.isActive ? 1 : 0.5 }}
                        >
                          {badges.length > 0 && (
                            <div slot="subtitle" className="adv-tables-badges">
                              {badges.map((b) => (
                                <span
                                  key={b.label}
                                  className={`adv-tables-badge adv-tables-badge--${b.tone}`}
                                >
                                  {b.label}
                                </span>
                              ))}
                            </div>
                          )}
                          {editMode && (
                            <i
                              slot="media"
                              className="f7-icons"
                              style={{
                                color: isSelected
                                  ? 'var(--f7-theme-color)'
                                  : 'var(--f7-list-item-subtitle-text-color)',
                                fontSize: 22,
                              }}
                            >
                              {isSelected ? 'checkmark_circle_fill' : 'circle'}
                            </i>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </div>
              ))}
            </div>
          );
        })}

      {editMode && selected.size > 0 && (
        <Toolbar bottom>
          <div className="left" />
          <div className="right">
            <F7Link style={{ color: 'var(--f7-color-red)' }} onClick={deleteSelected}>
              {rs.deleteCount(selected.size)}
            </F7Link>
          </div>
        </Toolbar>
      )}

      <BatchCreateSheet open={batchOpen} onClose={() => setBatchOpen(false)} zoneId={batchZoneId} />
    </>
  );
}

// ─── Floor Plan tab ───────────────────────────────────────────────────────────

const TABLE_W = 64;
const TABLE_H = 64;
const TABLE_FONT = 11;

type TableElement = Extract<FloorPlanElement, { kind: 'table' }>;

// A tiny top-down "table with chairs" mark, used in chips. Inherits `color`.
function TableGlyph() {
  return (
    <svg
      className="table-glyph"
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      aria-hidden="true"
    >
      <rect x="8" y="1.5" width="6" height="3" rx="1.5" fill="currentColor" opacity="0.38" />
      <rect x="8" y="17.5" width="6" height="3" rx="1.5" fill="currentColor" opacity="0.38" />
      <rect x="1.5" y="8" width="3" height="6" rx="1.5" fill="currentColor" opacity="0.38" />
      <rect x="17.5" y="8" width="3" height="6" rx="1.5" fill="currentColor" opacity="0.38" />
      <rect x="5" y="5" width="12" height="12" rx="3" fill="currentColor" />
    </svg>
  );
}

function TableNode({
  el,
  table,
  zoneLabel,
  isSelected,
  onSelect,
  onChange,
}: {
  el: TableElement;
  table: RestaurantTable | undefined;
  zoneLabel: string | null;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<TableElement>) => void;
}) {
  const isCircle = el.shape === 'circle';
  const w = el.width;
  const h = el.height;
  const label = table ? tableDisplayName(table, zoneLabel) : el.tableId.slice(-4);
  const seatCount = table?.seats ?? 0;
  const chairFill = isSelected ? '#93c5fd' : '#cbd5e1';
  const bodyFill = isSelected ? '#3b82f6' : '#e2e8f0';
  const bodyStroke = isSelected ? '#2563eb' : '#94a3b8';

  return (
    <Group
      x={el.x}
      y={el.y}
      width={w}
      height={h}
      rotation={el.rotation}
      draggable
      onClick={onSelect}
      onTap={onSelect}
      // Snap in layer (world) coords - e.target.x()/y() are relative to the
      // Layer, which has no transform of its own, so they're always in the same
      // coord space regardless of pan/zoom. `dragBoundFunc` instead gets the
      // *absolute* position (post-stage-transform), which means two tables
      // dragged at different zoom/pan land on different "grids" - hence the
      // "I moved them and they don't line up" symptom.
      onDragMove={(e) => {
        e.target.position({ x: snap(e.target.x()), y: snap(e.target.y()) });
      }}
      onDragEnd={(e) => onChange({ x: snap(e.target.x()), y: snap(e.target.y()) })}
    >
      {/* chairs first so the table body draws on top of them */}
      {isCircle
        ? circleChairs(w, h, seatCount).map((c) => (
            <Rect
              key={c.key}
              x={c.x}
              y={c.y}
              width={c.width}
              height={c.height}
              offsetX={c.offsetX}
              offsetY={c.offsetY}
              rotation={c.rotation}
              cornerRadius={2.5}
              fill={chairFill}
            />
          ))
        : rectChairs(w, h, seatCount).map((c) => (
            <Rect
              key={c.key}
              x={c.x}
              y={c.y}
              width={c.width}
              height={c.height}
              cornerRadius={2.5}
              fill={chairFill}
            />
          ))}

      {isCircle ? (
        <Circle
          x={w / 2}
          y={h / 2}
          radius={Math.min(w, h) / 2}
          fill={bodyFill}
          stroke={bodyStroke}
          strokeWidth={isSelected ? 2 : 1.25}
        />
      ) : (
        <Rect
          width={w}
          height={h}
          fill={bodyFill}
          stroke={bodyStroke}
          strokeWidth={isSelected ? 2 : 1.25}
          cornerRadius={8}
        />
      )}
      {/* the chairs already convey the seat count, so the body just needs the name */}
      <Text
        x={3}
        y={h / 2 - (TABLE_FONT + 1) * 0.6}
        width={w - 6}
        align="center"
        text={label}
        fontSize={TABLE_FONT + 1}
        fontStyle="bold"
        wrap="none"
        ellipsis
        fill={isSelected ? '#fff' : '#1e293b'}
        fontFamily="system-ui, sans-serif"
        listening={false}
      />
    </Group>
  );
}

function FloorPlanContent({
  onDirtyChange,
  saveRef,
}: {
  onDirtyChange: (dirty: boolean) => void;
  saveRef: React.MutableRefObject<(() => void) | null>;
}) {
  const t = useT();
  const rs = t.reservationSetup;
  const { data: floorPlan, isLoading: planLoading } = useFloorPlan();
  const { data: tables } = useTables();
  const { data: zones } = useZones();
  const updateFloorPlan = useUpdateFloorPlan();

  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const pinchRef = useRef<{ dist: number } | null>(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [elements, setElements] = useState<FloorPlanElement[]>([]);
  const [dirty, setDirty] = useState(false);
  const [pickedZoneId, setPickedZoneId] = useState<string | null | undefined>(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Pan/zoom of the canvas (stage transform). Tables stay in layout coords.
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });

  const hasSize = stageSize.w > 0 && stageSize.h > 0;

  useEffect(() => {
    if (floorPlan) setElements(floorPlan.layout.elements);
  }, [floorPlan]);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Synchronous read: forces layout now so stageSize is set before first paint.
    // Without this, ResizeObserver fires asynchronously and the first paint shows
    // an empty canvas (stageSize=0) because F7's Page effect hasn't set
    // --f7-page-navbar-offset yet, making the container temporarily 0-height.
    const { offsetWidth, offsetHeight } = el;
    if (offsetWidth > 0 && offsetHeight > 0) {
      setStageSize({ w: offsetWidth, h: offsetHeight });
    }
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setStageSize({ w: Math.round(width), h: Math.round(height) });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const tableElements = elements.filter((el): el is TableElement => el.kind === 'table');
  const placedIds = new Set(tableElements.map((el) => el.tableId));
  const allTables = tables ?? [];
  const tableById = useMemo(() => new Map((tables ?? []).map((t) => [t.id, t])), [tables]);

  // Zone tabs: every zone in sort order, plus a "No zone" bucket whenever any
  // table has no zone (or there are no zones defined at all).
  const zoneOptions = useMemo(() => {
    const opts = (zones ?? [])
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((z) => ({ id: z.id as string | null, label: z.label }));
    if (opts.length === 0 || (tables ?? []).some((t) => t.zoneId === null)) {
      opts.push({ id: null, label: rs.noZoneLabel });
    }
    return opts;
  }, [zones, tables, rs.noZoneLabel]);

  const activeZoneId =
    pickedZoneId !== undefined && zoneOptions.some((z) => z.id === pickedZoneId)
      ? pickedZoneId
      : (zoneOptions[0]?.id ?? null);
  const activeZoneLabel =
    activeZoneId === null ? null : (zoneOptions.find((z) => z.id === activeZoneId)?.label ?? null);

  const zoneTableElements = tableElements.filter(
    (el) => (tableById.get(el.tableId)?.zoneId ?? null) === activeZoneId,
  );
  const unplaced = allTables.filter(
    (t) => (t.zoneId ?? null) === activeZoneId && !placedIds.has(t.id),
  );

  // Close the "place a table" sheet once there's nothing left to place.
  useEffect(() => {
    if (unplaced.length === 0) setPickerOpen(false);
  }, [unplaced.length]);

  const switchZone = (id: string | null) => {
    setPickedZoneId(id);
    setSelectedId(null);
  };

  const placeTable = (t: RestaurantTable) => {
    if (!hasSize) return;
    // Drop the table at the centre of whatever the user is currently looking at,
    // cascaded a little so a burst of placements doesn't land in one stack.
    const cx = (stageSize.w / 2 - view.x) / view.scale;
    const cy = (stageSize.h / 2 - view.y) / view.scale;
    const step = (tableElements.length % 6) * SNAP;
    const newEl: TableElement = {
      kind: 'table',
      tableId: t.id,
      x: snap(cx - TABLE_W / 2 + step),
      y: snap(cy - TABLE_H / 2 + step),
      width: TABLE_W,
      height: TABLE_H,
      rotation: 0,
      shape: 'rect',
    };
    setElements((prev) => [...prev, newEl]);
    setDirty(true);
  };

  // Auto-place every unplaced table in this zone:
  //   1. Group tables by seat count. Each group becomes a compact roughly-square
  //      *cluster* (⌈√n⌉ columns, rows-as-needed) so same-`n` tables sit close
  //      together - not a single long row.
  //   2. Clusters are stacked vertically, smaller-seat clusters on top, each
  //      centred horizontally on the current viewport.
  //   3. The whole stack is shifted down - one cell at a time - until none of
  //      the new tables would overlap an already-placed table in this zone.
  //   4. All coordinates land on the SNAP grid.
  const autoPlaceAll = () => {
    if (!hasSize || unplaced.length === 0) return;
    const cx = (stageSize.w / 2 - view.x) / view.scale;
    const cy = (stageSize.h / 2 - view.y) / view.scale;
    const gap = SNAP * 2;
    const cellW = TABLE_W + gap;
    const cellH = TABLE_H + gap;

    // Group by seat count, sort groups ascending; sort within group by ordinal.
    const bySeats = new Map<number, RestaurantTable[]>();
    for (const t of unplaced) {
      const arr = bySeats.get(t.seats) ?? [];
      arr.push(t);
      bySeats.set(t.seats, arr);
    }
    const sortedGroups = [...bySeats.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, tables]) => tables.slice().sort((a, b) => a.ordinal - b.ordinal));

    // For each group, lay it out as a roughly-square sub-grid.
    const clusters = sortedGroups.map((tables) => {
      const n = tables.length;
      const cols = Math.max(1, Math.ceil(Math.sqrt(n)));
      const rows = Math.ceil(n / cols);
      const blockW = cols * TABLE_W + (cols - 1) * gap;
      const blockH = rows * TABLE_H + (rows - 1) * gap;
      return { tables, cols, blockW, blockH };
    });

    // Stack clusters vertically. Each cluster centred on x = 0 (layout origin),
    // y increases as we stack downwards.
    const layout: { table: RestaurantTable; dx: number; dy: number }[] = [];
    let cursorY = 0;
    clusters.forEach(({ tables, cols, blockW, blockH }) => {
      tables.forEach((t, i) => {
        layout.push({
          table: t,
          dx: -blockW / 2 + (i % cols) * cellW,
          dy: cursorY + Math.floor(i / cols) * cellH,
        });
      });
      cursorY += blockH + gap;
    });
    const totalH = clusters.reduce((s, c, i) => s + c.blockH + (i > 0 ? gap : 0), 0);

    const originX = cx;
    let originY = cy - totalH / 2;

    // Shift the whole block down in cellH steps until no new table (at its
    // final snapped position) overlaps an existing one.
    const overlaps = (ax: number, ay: number, ex: TableElement) =>
      !(
        ax + TABLE_W <= ex.x ||
        ex.x + ex.width <= ax ||
        ay + TABLE_H <= ex.y ||
        ex.y + ex.height <= ay
      );
    const collidesAt = (oy: number) =>
      layout.some(({ dx, dy }) => {
        const sx = snap(originX + dx);
        const sy = snap(oy + dy);
        return zoneTableElements.some((ex) => overlaps(sx, sy, ex));
      });
    for (let i = 0; i < 200 && collidesAt(originY); i++) {
      originY += cellH;
    }

    const newEls: TableElement[] = layout.map(({ table, dx, dy }) => ({
      kind: 'table',
      tableId: table.id,
      x: snap(originX + dx),
      y: snap(originY + dy),
      width: TABLE_W,
      height: TABLE_H,
      rotation: 0,
      shape: 'rect',
    }));
    setElements((prev) => [...prev, ...newEls]);
    setDirty(true);
    setPickerOpen(false);
  };

  const updateElement = useCallback((tableId: string, patch: Partial<TableElement>) => {
    setElements((prev) =>
      prev.map((el) => (el.kind === 'table' && el.tableId === tableId ? { ...el, ...patch } : el)),
    );
    setDirty(true);
  }, []);

  // Expose save and remove-selected to parent via refs
  saveRef.current = () => {
    if (!floorPlan || !hasSize) return;
    updateFloorPlan.mutate(
      {
        version: floorPlan.version,
        layout: {
          ...floorPlan.layout,
          canvas: { ...floorPlan.layout.canvas, width: stageSize.w, height: stageSize.h },
          elements,
        },
      },
      {
        onSuccess: () => {
          setDirty(false);
          f7.toast.show({ text: rs.floorPlanSaved, closeTimeout: 2000 });
        },
        onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.failedSaveFloorPlan),
      },
    );
  };

  const removeSelected = () => {
    if (!selectedId) return;
    setElements((prev) => prev.filter((el) => !(el.kind === 'table' && el.tableId === selectedId)));
    setSelectedId(null);
    setDirty(true);
  };

  const deselect = () => setSelectedId(null);

  // Zoom by `factor`, keeping the screen point `p` (in container coords) fixed.
  const zoomByFactor = (factor: number, p: { x: number; y: number }) => {
    setView((v) => {
      const scale = clampScale(v.scale * factor);
      const real = scale / v.scale;
      return { scale, x: p.x - (p.x - v.x) * real, y: p.y - (p.y - v.y) * real };
    });
  };
  const stageCenter = () => ({ x: stageSize.w / 2, y: stageSize.h / 2 });
  const resetView = () => setView({ x: 0, y: 0, scale: 1 });
  const fitView = () => {
    if (!hasSize || zoneTableElements.length === 0) {
      resetView();
      return;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const el of zoneTableElements) {
      minX = Math.min(minX, el.x - CHAIR_THICK - CHAIR_GAP);
      minY = Math.min(minY, el.y - CHAIR_THICK - CHAIR_GAP);
      maxX = Math.max(maxX, el.x + el.width + CHAIR_THICK + CHAIR_GAP);
      maxY = Math.max(maxY, el.y + el.height + CHAIR_THICK + CHAIR_GAP);
    }
    const pad = 32;
    const bw = maxX - minX + pad * 2;
    const bh = maxY - minY + pad * 2;
    const scale = clampScale(Math.min(stageSize.w / bw, stageSize.h / bh, 1.5));
    setView({
      scale,
      x: (stageSize.w - bw * scale) / 2 - (minX - pad) * scale,
      y: (stageSize.h - bh * scale) / 2 - (minY - pad) * scale,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {!planLoading && zoneOptions.length > 0 && (
        <div className="floor-toolbar">
          <button
            type="button"
            className="floor-zone-chip"
            onClick={() => {
              f7.actions
                .create({
                  buttons: [
                    zoneOptions.map((z) => ({
                      text: z.label,
                      bold: z.id === activeZoneId,
                      onClick: () => switchZone(z.id),
                    })),
                    [{ text: t.cancel, color: 'red' }],
                  ],
                })
                .open();
            }}
          >
            <span>{activeZoneLabel ?? rs.noZoneLabel}</span>
            <i className="f7-icons">chevron_down</i>
          </button>
          <div className="floor-toolbar-spacer" />
          {unplaced.length > 0 && (
            <button type="button" className="floor-add-btn" onClick={() => setPickerOpen(true)}>
              <i className="f7-icons">plus</i>
              <span>{rs.addCount(unplaced.length)}</span>
            </button>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          flex: 1,
          background: '#f1f5f9',
          overflow: 'hidden',
          touchAction: 'none',
          margin: '0 16px 16px',
          borderRadius: 16,
          border: '1px solid var(--f7-list-border-color, #e2e8f0)',
        }}
      >
        {planLoading && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Preloader />
          </div>
        )}
        {!planLoading && hasSize && (
          <Stage
            ref={stageRef}
            width={stageSize.w}
            height={stageSize.h}
            x={view.x}
            y={view.y}
            scaleX={view.scale}
            scaleY={view.scale}
            draggable
            onMouseDown={(e) => {
              if (e.target === e.target.getStage()) deselect();
            }}
            onTouchStart={(e) => {
              if (e.evt.touches.length === 1 && e.target === e.target.getStage()) deselect();
            }}
            onDragEnd={(e) => {
              const stage = e.target.getStage();
              if (stage && e.target === stage) {
                setView((v) => ({ ...v, x: stage.x(), y: stage.y() }));
              }
            }}
            onWheel={(e) => {
              e.evt.preventDefault();
              const pointer = stageRef.current?.getPointerPosition();
              if (pointer) zoomByFactor(e.evt.deltaY > 0 ? 1 / 1.12 : 1.12, pointer);
            }}
            onTouchMove={(e) => {
              if (e.evt.touches.length !== 2) return;
              e.evt.preventDefault();
              const stage = stageRef.current;
              if (!stage) return;
              if (stage.isDragging()) stage.stopDrag();
              const [t1, t2] = [e.evt.touches[0], e.evt.touches[1]];
              const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
              const rect = stage.container().getBoundingClientRect();
              const center = {
                x: (t1.clientX + t2.clientX) / 2 - rect.left,
                y: (t1.clientY + t2.clientY) / 2 - rect.top,
              };
              if (pinchRef.current) zoomByFactor(dist / pinchRef.current.dist, center);
              pinchRef.current = { dist };
            }}
            onTouchEnd={() => {
              pinchRef.current = null;
            }}
          >
            <Layer>
              {zoneTableElements.map((el) => (
                <TableNode
                  key={el.tableId}
                  el={el}
                  table={tableById.get(el.tableId)}
                  zoneLabel={activeZoneLabel}
                  isSelected={selectedId === el.tableId}
                  onSelect={() =>
                    setSelectedId((prev) => (prev === el.tableId ? null : el.tableId))
                  }
                  onChange={(patch) => updateElement(el.tableId, patch)}
                />
              ))}
            </Layer>
          </Stage>
        )}

        {!planLoading && hasSize && (
          <div className="canvas-zoom">
            <div className="canvas-zoom-group">
              <button
                type="button"
                className="canvas-zoom-btn"
                aria-label={rs.zoomIn}
                onClick={() => zoomByFactor(1.2, stageCenter())}
              >
                +
              </button>
              <button
                type="button"
                className="canvas-zoom-btn"
                aria-label={rs.zoomOut}
                onClick={() => zoomByFactor(1 / 1.2, stageCenter())}
              >
                −
              </button>
            </div>
            <button
              type="button"
              className="canvas-zoom-btn canvas-zoom-fit"
              aria-label={rs.fitToTables}
              onClick={fitView}
            >
              ⤢
            </button>
          </div>
        )}
      </div>

      {!planLoading && selectedId && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '8px 16px',
            background: 'rgba(248,250,252,0.95)',
            borderTop: '1px solid var(--f7-list-border-color)',
            flexShrink: 0,
          }}
        >
          <button
            onClick={removeSelected}
            style={{
              background: 'var(--f7-color-red)',
              color: '#fff',
              border: 'none',
              borderRadius: 50,
              padding: '8px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            }}
          >
            {rs.removeTable}
          </button>
        </div>
      )}

      <PushSheet open={pickerOpen} onClose={() => setPickerOpen(false)} style={{ height: 'auto' }}>
        <Toolbar>
          <div className="left">
            <F7Link sheetClose>{rs.done}</F7Link>
          </div>
          <div className="title">
            {rs.tablesToPlace(unplaced.length, unplaced.length === 1 ? '' : 's')}
          </div>
          <div className="right">
            {unplaced.length > 0 && <F7Link onClick={autoPlaceAll}>{rs.autoPlace}</F7Link>}
          </div>
        </Toolbar>
        <PageContent style={{ maxHeight: '55vh' }}>
          <div className="unplaced-grid">
            {unplaced.map((t) => (
              <button
                key={t.id}
                type="button"
                className="unplaced-chip"
                onClick={() => {
                  placeTable(t);
                  setPickerOpen(false);
                }}
              >
                <TableGlyph />
                <span className="unplaced-chip-name">{tableDisplayName(t, activeZoneLabel)}</span>
              </button>
            ))}
          </div>
        </PageContent>
      </PushSheet>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
//
// Tablets and desktops get a true split view: Tables list in a left sidebar,
// Floor Plan canvas filling the rest - the iPad/macOS Notes/Files pattern.
// Phones keep the segmented Tables / Floor Plan tabs. Switch-over at 900px
// (see `useIsWideViewport`) gives the floor plan enough breathing room.

function AdvancedTablesPage() {
  const t = useT();
  const rs = t.reservationSetup;
  const isWide = useIsWideViewport();
  const [tab, setTab] = useState<'tables' | 'floor-plan'>('tables');

  // Tables tab state surfaced to navbar
  const [editMode, setEditMode] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [batchOpen, setBatchOpen] = useState(false);

  const [fpDirty, setFpDirty] = useState(false);
  const fpSaveRef = useRef<(() => void) | null>(null);

  const handleTabChange = (next: 'tables' | 'floor-plan') => {
    setTab(next);
    setEditMode(false);
    setSelectedCount(0);
  };

  // Navbar trailing actions. On wide layouts both panels are visible, so both
  // contexts' actions live here together (with a small separator).
  const tablesActions = (
    <>
      {editMode && selectedCount === 0 && (
        <F7Link iconF7="plus_circle" onClick={() => setBatchOpen(true)} />
      )}
      <F7Link iconF7={editMode ? 'checkmark' : 'pencil'} onClick={() => setEditMode((m) => !m)} />
    </>
  );
  const floorPlanActions = fpDirty ? (
    <F7Link onClick={() => fpSaveRef.current?.()}>{t.saveShort}</F7Link>
  ) : null;

  const navRight = isWide ? (
    <>
      {tablesActions}
      {floorPlanActions}
    </>
  ) : tab === 'tables' ? (
    tablesActions
  ) : (
    floorPlanActions
  );

  // Render both panels side-by-side on wide screens; tabbed on phones.
  const tablesPanel = (
    <TablesContent
      editMode={editMode}
      onEditModeChange={setEditMode}
      onSelectedCountChange={setSelectedCount}
    />
  );
  const floorPlanPanel = <FloorPlanContent onDirtyChange={setFpDirty} saveRef={fpSaveRef} />;

  return (
    <Page name="advanced-tables" pageContent={false}>
      <Navbar title={rs.advancedTables} backLink={t.back}>
        {navRight && <NavRight>{navRight}</NavRight>}
      </Navbar>

      <PageContent
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: '100%',
          paddingBottom: 'calc(var(--f7-safe-area-bottom, 0px) + 64px)',
        }}
      >
        {isWide ? (
          <div className="adv-tables-split">
            <aside className="adv-tables-sidebar">{tablesPanel}</aside>
            <section className="adv-tables-detail">{floorPlanPanel}</section>
          </div>
        ) : (
          <>
            <div style={{ padding: '8px 16px', flexShrink: 0 }}>
              <Segmented strong>
                <Button active={tab === 'tables'} onClick={() => handleTabChange('tables')}>
                  {rs.tables}
                </Button>
                <Button active={tab === 'floor-plan'} onClick={() => handleTabChange('floor-plan')}>
                  {rs.floorPlan}
                </Button>
              </Segmented>
            </div>

            {tab === 'tables' && <div style={{ flex: 1, overflowY: 'auto' }}>{tablesPanel}</div>}

            {tab === 'floor-plan' && (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  minHeight: 0,
                }}
              >
                {floorPlanPanel}
              </div>
            )}
          </>
        )}
      </PageContent>

      <BatchCreateSheet open={batchOpen} onClose={() => setBatchOpen(false)} zoneId={null} />
    </Page>
  );
}

export default AdvancedTablesPage;
